import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatBytes } from "@/lib/format";
import { api, http } from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UploadCloud } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

export default function UploadPage() {
  const qc = useQueryClient();
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list() });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: () => api.cameras.list() });

  const [projectId, setProjectId] = useState("");
  const [cameraId, setCameraId] = useState("");
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const filteredCameras = useMemo(() => {
    if (!cameras) return [];
    if (!projectId) return cameras;
    return cameras.filter((c) => c.projectId === Number(projectId) || c.projectId === null);
  }, [cameras, projectId]);

  const totalBytes = files.reduce((acc, f) => acc + f.size, 0);

  const handleUpload = async () => {
    if (!projectId || !cameraId) { toast.error("Selectați proiectul și camera"); return; }
    if (files.length === 0) { toast.error("Selectați cel puțin un fișier"); return; }
    setUploading(true); setProgress(0); setResult(null);
    try {
      const fd = new FormData();
      fd.append("projectId", projectId);
      fd.append("cameraId", cameraId);
      files.forEach((f) => fd.append("files", f));
      const res = await http.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => { if (e.total) setProgress((e.loaded / e.total) * 100); },
        timeout: 600000,
      });
      const data = res.data;
      setResult({ clipsCreated: data.clipsCreated, days: data.days });
      toast.success(`${data.clipsCreated} clipuri importate · filme zilnice regenerate pentru ${data.days.length} zi/zile.`);
      setFiles([]);
      qc.invalidateQueries({ queryKey: ["stats"] });
      qc.invalidateQueries({ queryKey: ["clips"] });
      qc.invalidateQueries({ queryKey: ["films"] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Eroare la încărcare");
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col gap-6 max-w-3xl" data-testid="upload-page">
        <header>
          <h1 className="text-2xl lg:text-3xl font-semibold tracking-tight">Încărcare arhive</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Încărcați arhive ZIP din cloud-ul Reolink sau fișiere MP4/MOV. Fișierele sunt stocate în siguranță,
            iar filmul zilnic per cameră se regenerează automat. Detecția AI rulează în fundal.
          </p>
        </header>

        <Card>
          <CardHeader><CardTitle className="text-base">Detalii încărcare</CardTitle></CardHeader>
          <CardContent className="grid gap-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Proiect (șantier)</Label>
                <Select value={projectId} onValueChange={(v) => { setProjectId(v); setCameraId(""); }}>
                  <SelectTrigger data-testid="upload-project-select"><SelectValue placeholder="Selectați șantier" /></SelectTrigger>
                  <SelectContent>{projects?.filter((p) => p.status !== "archived").map((p) => <SelectItem key={p.id} value={String(p.id)}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid gap-1.5"><Label>Cameră</Label>
                <Select value={cameraId} onValueChange={setCameraId} disabled={!projectId}>
                  <SelectTrigger data-testid="upload-camera-select"><SelectValue placeholder={projectId ? "Selectați camera" : "Selectați mai întâi proiectul"} /></SelectTrigger>
                  <SelectContent>{filteredCameras.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>

            <div className="grid gap-1.5">
              <Label>Fișiere (ZIP, MP4, MOV)</Label>
              <Input type="file" multiple accept=".zip,.mp4,.mov,.m4v" onChange={(e) => setFiles(Array.from(e.target.files ?? []))} data-testid="upload-file-input" />
              {files.length ? <p className="text-xs text-muted-foreground">{files.length} fișiere · {formatBytes(totalBytes)}</p> : <p className="text-xs text-muted-foreground">Puteți selecta mai multe arhive simultan.</p>}
            </div>

            {uploading && (
              <div className="flex flex-col gap-2"><Progress value={progress} /><p className="text-xs text-muted-foreground">Se încarcă și se procesează... {progress.toFixed(0)}%</p></div>
            )}

            <div className="flex items-center gap-3">
              <Button onClick={handleUpload} disabled={uploading} className="gap-1.5" data-testid="upload-submit-btn">
                {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Se procesează...</> : <><UploadCloud className="h-4 w-4" /> Încarcă & generează film zilnic</>}
              </Button>
            </div>

            {result && (
              <div className="rounded-md border bg-muted/40 p-3 text-sm" data-testid="upload-result">
                <p className="font-medium">Import finalizat</p>
                <p className="text-muted-foreground">{result.clipsCreated} clipuri salvate · {result.days.length} zi/zile actualizate{result.days.length ? ` (${result.days.join(", ")})` : ""}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
